(() => {
  const RULE_SELECTOR = 'script[type="application/json"][id^="AutoAddRule-"]';
  const inProgress = new Set(); // per-rule lock to prevent loops

  // Single-flight lock for cart checks (prevents double-add races)
  let cartCheckRunning = false;
  let cartCheckQueued = false;

  function getRules() {
    return Array.from(document.querySelectorAll(RULE_SELECTOR))
      .map((n) => {
        try { return JSON.parse(n.textContent || '{}'); } catch (e) { return null; }
      })
      .filter((r) => r && r.triggerCollectionHandle);
  }

  function log(rule, ...args) {
    if (rule?.debug) console.log('[AutoAddRule]', ...args);
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  function cartHasVariant(cart, variantId) {
    return !!cart?.items?.some((i) => Number(i.id) === Number(variantId));
  }

  function cartHasAttribute(cart, key) {
    return cart?.attributes && cart.attributes[key];
  }

  function getCartCurrency(cart) {
    // Try common sources in Shopify themes/Markets setups
    return (
      (cart && (cart.currency || cart.currency_code)) ||
      window?.Shopify?.currency?.active ||
      window?.Shopify?.currency?.current ||
      document.documentElement?.getAttribute('data-currency') ||
      document.documentElement?.dataset?.currency ||
      ''
    ).toString().toUpperCase();
  }

  function parseCurrencyThresholds(text) {
    // Accept lines like: "EUR=100", "DKK:750", "USD 110"
    const map = {};
    if (!text) return map;

    const parts = String(text)
      .split(/[\n,;]+/g)
      .map((s) => s.trim())
      .filter(Boolean);

    for (const p of parts) {
      const m = p.match(/^([A-Za-z]{3})\s*[:= ]\s*([0-9]+(?:\.[0-9]+)?)$/);
      if (!m) continue;
      map[m[1].toUpperCase()] = Number(m[2]);
    }
    return map;
  }

  function getMinTotalForCurrency(rule, currencyCode) {
    const map = parseCurrencyThresholds(rule.minCartTotalsByCurrency);
    const fromMap = map[currencyCode];
    const fallback = Number(rule.defaultMinCartTotal || 0);
    return Number.isFinite(fromMap) ? fromMap : fallback;
  }

  async function setCartAttribute(key, value) {
    const body = new URLSearchParams();
    body.append(`attributes[${key}]`, value);

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body
    });
    return res.json();
  }

  async function addVariants(variantIds) {
    const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });
    return res.json();
  }

  async function changeVariantQuantity(variantId, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
    });
    return res.json();
  }

  async function getCollectionProductIdSet(handle) {
    const key = `autoAdd_collection_${handle}`;
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) return new Set(JSON.parse(cached));
    } catch (e) {}

    const res = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`, {
      credentials: 'same-origin'
    });
    const data = await res.json();
    const ids = (data.products || []).map((p) => Number(p.id));

    try { sessionStorage.setItem(key, JSON.stringify(ids)); } catch (e) {}
    return new Set(ids);
  }

  async function handleCartState() {
    // Single-flight: collapse multiple triggers into one run
    if (cartCheckRunning) {
      cartCheckQueued = true;
      return;
    }
    cartCheckRunning = true;

    try {
      const rules = getRules();
      if (!rules.length) return;

      const cart = await getCart();
      const cartProductIds = new Set((cart.items || []).map((i) => Number(i.product_id)));
      const currency = getCartCurrency(cart);

      for (const rule of rules) {
        const attrKey = `auto_add_done_${rule.ruleId}`;
        if (inProgress.has(rule.ruleId)) continue;

        const triggerSet = await getCollectionProductIdSet(rule.triggerCollectionHandle);
        const triggeredNow = Array.from(cartProductIds).some((pid) => triggerSet.has(pid));

        const gifts = (rule.giftVariants || []).filter((v) => v != null).map(Number);
        if (!gifts.length) continue;

        const giftLines = (cart.items || []).filter((i) => gifts.includes(Number(i.id)));
        const missing = gifts.filter((v) => !cartHasVariant(cart, v));

        // --------- MIN CART TOTAL GATE ----------
        // cart.total_price is in minor units (cents)
        const minTotalMajor = getMinTotalForCurrency(rule, currency);
        const minTotalMinor = Math.round(minTotalMajor * 100);
        const cartTotalMinor = Number(cart.total_price || 0);

        const underMin = cartTotalMinor < minTotalMinor;

        // If under minimum: remove gifts (if present) and clear done flag
        if (underMin) {
          if (giftLines.length || cartHasAttribute(cart, attrKey)) {
            inProgress.add(rule.ruleId);
            try {
              log(rule, `Under minimum (${currency} ${minTotalMajor}). Removing gifts / clearing flag.`);
              // Remove gifts from cart
              for (const line of giftLines) {
                await changeVariantQuantity(line.id, 0);
              }
              // Clear attribute so it can trigger later when total qualifies
              await setCartAttribute(attrKey, '');
              document.dispatchEvent(new CustomEvent('cart:build'));
            } finally {
              inProgress.delete(rule.ruleId);
            }
          }
          continue;
        }
        // ----------------------------------------

        // RESET CASE A: no trigger items in cart
        if (!triggeredNow) {
          if (cartHasAttribute(cart, attrKey)) {
            inProgress.add(rule.ruleId);
            try {
              log(rule, 'Resetting done flag (no trigger items in cart)');
              await setCartAttribute(attrKey, '');
            } finally {
              inProgress.delete(rule.ruleId);
            }
          }
          continue;
        }

        // RESET CASE B: trigger items exist, but gifts were removed
        if (cartHasAttribute(cart, attrKey) && missing.length > 0) {
          inProgress.add(rule.ruleId);
          try {
            log(rule, 'Gift missing; clearing done flag so it can be added again');
            await setCartAttribute(attrKey, '');
          } finally {
            inProgress.delete(rule.ruleId);
          }
        }

        // Clamp gift qty to 1 (prevents accidental doubles)
        for (const line of giftLines) {
          if (Number(line.quantity) > 1) {
            inProgress.add(rule.ruleId);
            try {
              log(rule, 'Clamping gift qty to 1 for variant', line.id);
              await changeVariantQuantity(line.id, 1);
            } finally {
              inProgress.delete(rule.ruleId);
            }
          }
        }

        // Re-fetch cart after possible changes (keeps logic accurate)
        const cart2 = await getCart();
        const cart2Currency = getCartCurrency(cart2);
        const cart2TotalMinor = Number(cart2.total_price || 0);

        // Re-check minimum after refresh
        const min2Major = getMinTotalForCurrency(rule, cart2Currency);
        const min2Minor = Math.round(min2Major * 100);
        if (cart2TotalMinor < min2Minor) continue;

        const cart2ProductIds = new Set((cart2.items || []).map((i) => Number(i.product_id)));
        const triggeredNow2 = Array.from(cart2ProductIds).some((pid) => triggerSet.has(pid));
        if (!triggeredNow2) continue;

        const missing2 = gifts.filter((v) => !cartHasVariant(cart2, v));
        if (missing2.length === 0) {
          if (!cartHasAttribute(cart2, attrKey)) {
            inProgress.add(rule.ruleId);
            try { await setCartAttribute(attrKey, '1'); } finally { inProgress.delete(rule.ruleId); }
          }
          continue;
        }

        // Add missing gifts once, then mark done
        inProgress.add(rule.ruleId);
        try {
          log(rule, 'Triggered. Adding missing gifts:', missing2);
          await addVariants(missing2);
          await setCartAttribute(attrKey, '1');
          document.dispatchEvent(new CustomEvent('cart:build'));
        } finally {
          inProgress.delete(rule.ruleId);
        }
      }
    } finally {
      cartCheckRunning = false;
      if (cartCheckQueued) {
        cartCheckQueued = false;
        handleCartState().catch(() => {});
      }
    }
  }

  // Run on initial load
  document.addEventListener('DOMContentLoaded', () => {
    handleCartState().catch(() => {});
  });

  // Impulse/Archetype events
  document.addEventListener('ajaxProduct:added', () => handleCartState().catch(() => {}));
  document.addEventListener('cart:updated', () => handleCartState().catch(() => {}));
  document.addEventListener('cart:build', () => handleCartState().catch(() => {}));

  // Catch fetch-based cart writes
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await _fetch(...args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        setTimeout(() => handleCartState().catch(() => {}), 50);
      }
    } catch (e) {}
    return res;
  };

  // Catch XHR-based cart writes
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._autoAdd_url = url;
    return _open.call(this, method, url, ...rest);
  };

  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', () => {
      const url = this._autoAdd_url || '';
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        setTimeout(() => handleCartState().catch(() => {}), 50);
      }
    });
    return _send.apply(this, args);
  };
})();
